import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Banknote, Eye, FileText, Plus, Printer, Search, Wallet } from "lucide-react";
import * as api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useData } from "@/hooks/useData";
import { Button, DataTable, Input, Loader, PageHeader, PersonCell, StatCard, StatusBadge, TableCard } from "@/components/ui";
import { openSalarySlip } from "@/lib/documents";
import { aed, currentMonth, monthLabel } from "@/lib/format";

const open = (slip, print) => { if (!openSalarySlip(slip, print)) toast.error("Allow pop-ups to view the slip"); };

export default function SalarySlips() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isEmployee = user.role === "employee";
  const [month, setMonth] = useState("");
  const [q, setQ] = useState("");
  const { data: slips } = useData(() => api.salarySlips.list(user, { month }), [user, month]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (slips || []).filter((s) => !needle || `${s.employee_name} ${s.employee_id} ${s.designation}`.toLowerCase().includes(needle));
  }, [slips, q]);

  if (!slips) return <Loader />;

  const totalNet = rows.reduce((s, x) => s + x.net_pay, 0);
  const columns = [
    ...(!isEmployee ? [{ key: "employee", header: "Employee", render: (s) => <PersonCell name={s.employee_name} sub={s.designation} /> }] : []),
    { key: "month", header: "Month", render: (s) => <span className="text-xs font-semibold">{monthLabel(s.month)}</span> },
    { key: "gross", header: "Gross", align: "right", hide: "md", render: (s) => <span className="text-xs tabular-nums">{aed(s.gross)}</span> },
    { key: "deductions", header: "Deductions", align: "right", hide: "lg", render: (s) => <span className="text-xs tabular-nums text-rose-600">{s.total_deductions ? `− ${aed(s.total_deductions)}` : "0"}</span> },
    { key: "net", header: "Net (AED)", align: "right", render: (s) => <span className="text-xs font-extrabold text-brand-blue tabular-nums">{aed(s.net_pay)}</span> },
    { key: "wps", header: "WPS", hide: "md", render: (s) => <StatusBadge status={s.wps_status} /> },
    { key: "actions", header: "", align: "right", render: (s) => (
      <div className="flex gap-1.5 justify-end">
        <Button size="icon" variant="ghost" onClick={() => open(s, false)} aria-label="View slip" title="View"><Eye size={14} /></Button>
        <Button size="icon" variant="ghost" onClick={() => open(s, true)} aria-label="Print or save as PDF" title="Print / PDF"><Printer size={14} /></Button>
      </div>
    ) },
  ];

  return (
    <>
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-3">
        <StatCard label="Slips" value={rows.length} icon={FileText} />
        <StatCard label="Total Net Pay" value={`${aed(totalNet / 1000, 1)}K`} sub="AED" icon={Wallet} color="green" />
        <StatCard label="Paid" value={rows.filter((s) => s.wps_status === "Paid").length} icon={Banknote} color="orange" className="col-span-2 lg:col-span-1" />
      </div>

      <PageHeader
        actions={user.role === "admin" && <Button variant="primary" icon={<Plus size={14} />} onClick={() => navigate("/payroll/slips/generate")}>Generate slip</Button>}
      >
        {!isEmployee && (
          <div className="flex-1 min-w-[220px]">
            <Input icon={Search} placeholder="Search by employee…" value={q} onChange={(e) => setQ(e.target.value)} className="rounded-full" aria-label="Search slips" />
          </div>
        )}
        <div className="flex items-center gap-2">
          <Input type="month" value={month} max={currentMonth()} onChange={(e) => setMonth(e.target.value)} className="sm:w-44" aria-label="Month" />
          {month && <Button variant="ghost" onClick={() => setMonth("")}>Clear</Button>}
        </div>
      </PageHeader>

      <TableCard heading={`${rows.length} slip${rows.length === 1 ? "" : "s"}`} aside={month ? monthLabel(month) : "All months"}>
        <DataTable columns={columns} rows={rows} empty={{ icon: FileText, title: "No salary slips", message: month ? "Nothing for this month." : undefined }} />
      </TableCard>
    </>
  );
}
